/**
 * Discord Context - Manages Discord connection state
 * 
 * Handles:
 * - Discord OAuth flow initiation
 * - Storing discord_id, discord_username in localStorage
 * - 24-hour dismissal for connection prompt
 * - Modal visibility state
 * - Integration with checkout flow (prompt before Tebex)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useCheckout } from './CheckoutContext';

const DiscordContext = createContext();

// localStorage keys
const DISCORD_USER_KEY = 'retrostore_discord_user';
const DISCORD_DISMISS_KEY = 'retrostore_discord_dismissed';
const DISCORD_PENDING_CHECKOUT_KEY = 'retrostore_discord_pending_checkout';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export const useDiscord = () => {
  const context = useContext(DiscordContext);
  if (!context) {
    throw new Error('useDiscord must be used within a DiscordProvider');
  }
  return context;
};

export const DiscordProvider = ({ children }) => {
  const { isAuthenticated, user, launchTebexOverlay } = useCheckout();
  
  // Discord user state from localStorage
  const [discordUser, setDiscordUser] = useState(() => {
    try {
      const saved = localStorage.getItem(DISCORD_USER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      localStorage.removeItem(DISCORD_USER_KEY);
      return null;
    }
  });
  
  // Modal visibility state
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Promise resolver for checkout flow integration
  const promptResolverRef = useRef(null);
  
  // Pending basket ident for checkout continuation after Discord OAuth
  const [pendingBasketIdent, setPendingBasketIdent] = useState(null);
  
  // Check if user has dismissed the prompt within 24 hours
  const isDismissed = useCallback(() => {
    try {
      const dismissedAt = localStorage.getItem(DISCORD_DISMISS_KEY);
      if (!dismissedAt) return false;
      
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      
      // If 24 hours have passed, clear the dismissal
      if (now - dismissedTime > DISMISS_DURATION_MS) {
        localStorage.removeItem(DISCORD_DISMISS_KEY);
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }, []);
  
  // Derived state
  const isConnected = !!discordUser?.discord_id;
  
  // Should show the connection prompt?
  // User is logged in + not connected to Discord + hasn't dismissed
  const shouldShowPrompt = isAuthenticated && !isConnected && !isDismissed();
  
  /**
   * Prompt user to connect Discord (used by checkout flow)
   * Returns a Promise that resolves when user connects or skips
   * 
   * @param {string|null} basketIdent - Optional basket ident for checkout continuation
   * @returns {Promise<{connected: boolean, skipped: boolean}>}
   */
  const promptForConnection = useCallback((basketIdent = null) => {
    return new Promise((resolve) => {
      // Already connected - resolve immediately
      if (isConnected) {
        resolve({ connected: true, skipped: false });
        return;
      }
      
      // User dismissed recently - skip the prompt
      if (isDismissed()) {
        resolve({ connected: false, skipped: true });
        return;
      }
      
      // Store the basket ident for potential OAuth redirect
      setPendingBasketIdent(basketIdent);
      
      // Store the resolver and show modal
      promptResolverRef.current = resolve;
      setIsModalOpen(true);
    });
  }, [isConnected, isDismissed]);
  
  // Dismiss the prompt for 24 hours
  const dismissPrompt = useCallback((dontShowAgain = false) => {
    if (dontShowAgain) {
      localStorage.setItem(DISCORD_DISMISS_KEY, String(Date.now()));
    }
    setIsModalOpen(false);
    
    // Resolve pending promise if exists (user skipped)
    if (promptResolverRef.current) {
      promptResolverRef.current({ connected: false, skipped: true });
      promptResolverRef.current = null;
    }
  }, []);
  
  // Open the modal manually (from navigation button)
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  
  // Close the modal (just close, no dismiss)
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    
    // Resolve pending promise if exists (user closed modal)
    if (promptResolverRef.current) {
      promptResolverRef.current({ connected: false, skipped: true });
      promptResolverRef.current = null;
    }
  }, []);
  
  // Initiate Discord OAuth flow
  // Passes FiveM username so backend can link FiveM ↔ Discord accounts
  // If basketIdent provided, stores it for post-OAuth checkout continuation
  const connectDiscord = useCallback((basketIdent = null) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.retrostore.dev';
    
    // Get FiveM username from checkout context
    const fivemUsername = user?.username || '';
    
    // If there's a pending checkout, store it for after OAuth
    if (basketIdent) {
      localStorage.setItem(DISCORD_PENDING_CHECKOUT_KEY, JSON.stringify({
        basketIdent,
        timestamp: Date.now()
      }));
    }
    
    // Build OAuth URL with FiveM username for linking
    const authUrl = new URL(`${backendUrl}/api/discord/auth`);
    if (fivemUsername) {
      authUrl.searchParams.set('fivem_username', fivemUsername);
    }
    
    // Redirect to backend which will redirect to Discord
    window.location.href = authUrl.toString();
  }, [user]);
  
  // Save Discord user data (called after OAuth callback)
  const saveDiscordUser = useCallback((userData) => {
    const discordUserData = {
      discord_id: userData.discord_id || userData.id,
      discord_username: userData.discord_username || userData.username,
      discord_avatar: userData.discord_avatar || userData.avatar,
      connected_at: new Date().toISOString()
    };
    
    localStorage.setItem(DISCORD_USER_KEY, JSON.stringify(discordUserData));
    setDiscordUser(discordUserData);
    
    // Clear any dismissal since user is now connected
    localStorage.removeItem(DISCORD_DISMISS_KEY);
    
    // Close the modal
    setIsModalOpen(false);
    
    // Resolve pending promise if exists (user connected)
    if (promptResolverRef.current) {
      promptResolverRef.current({ connected: true, skipped: false });
      promptResolverRef.current = null;
    }
  }, []);
  
  // Disconnect Discord (clear local data, doesn't revoke OAuth)
  const disconnectDiscord = useCallback(() => {
    localStorage.removeItem(DISCORD_USER_KEY);
    setDiscordUser(null);
  }, []);
  
  // Get pending checkout data (if exists)
  const getPendingCheckout = useCallback(() => {
    try {
      const pending = localStorage.getItem(DISCORD_PENDING_CHECKOUT_KEY);
      if (!pending) return null;
      
      const data = JSON.parse(pending);
      
      // Expire after 10 minutes
      if (Date.now() - data.timestamp > 10 * 60 * 1000) {
        localStorage.removeItem(DISCORD_PENDING_CHECKOUT_KEY);
        return null;
      }
      
      return data;
    } catch {
      localStorage.removeItem(DISCORD_PENDING_CHECKOUT_KEY);
      return null;
    }
  }, []);
  
  // Clear pending checkout
  const clearPendingCheckout = useCallback(() => {
    localStorage.removeItem(DISCORD_PENDING_CHECKOUT_KEY);
  }, []);
  
  // Handle OAuth callback result from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const discordId = params.get('discord_id');
    const discordUsername = params.get('discord_username');
    const discordAvatar = params.get('discord_avatar');
    const discordError = params.get('discord_error');
    
    if (discordError) {
      console.error('[Discord] OAuth error:', discordError);
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete('discord_error');
      window.history.replaceState({}, '', url.pathname + url.search);
      
      // Clear pending checkout on error
      clearPendingCheckout();
      return;
    }
    
    if (discordId && discordUsername) {
      saveDiscordUser({
        discord_id: discordId,
        discord_username: discordUsername,
        discord_avatar: discordAvatar
      });
      
      // Clean URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('discord_id');
      url.searchParams.delete('discord_username');
      url.searchParams.delete('discord_avatar');
      window.history.replaceState({}, '', url.pathname + url.search);
      
      // Check for pending checkout and continue if exists
      const pendingCheckout = getPendingCheckout();
      if (pendingCheckout && launchTebexOverlay) {
        console.log('[Discord] Resuming checkout after Discord connection');
        clearPendingCheckout();
        
        // Small delay to let state settle, then emit event for checkout to continue
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('discord-checkout-continue', {
            detail: { basketIdent: pendingCheckout.basketIdent }
          }));
        }, 300);
      }
    }
  }, [saveDiscordUser, getPendingCheckout, clearPendingCheckout, launchTebexOverlay]);
  
  // Listen for checkout flow requesting Discord prompt (with promise resolution)
  useEffect(() => {
    const handlePromptRequest = async (event) => {
      const { basketIdent, resolve } = event.detail;
      
      // Already connected - resolve immediately
      if (isConnected) {
        resolve({ connected: true, skipped: false });
        return;
      }
      
      // User dismissed recently - skip
      if (isDismissed()) {
        resolve({ connected: false, skipped: true });
        return;
      }
      
      // Store basket ident and show modal
      setPendingBasketIdent(basketIdent);
      promptResolverRef.current = resolve;
      setIsModalOpen(true);
    };
    
    window.addEventListener('discord-prompt-request', handlePromptRequest);
    return () => window.removeEventListener('discord-prompt-request', handlePromptRequest);
  }, [isConnected, isDismissed]);
  
  // Listen for simple "show modal" request after login (no promise, just show)
  useEffect(() => {
    const handleShowModal = () => {
      // Don't show if already connected or dismissed
      if (isConnected || isDismissed()) {
        return;
      }
      
      // Show the modal
      setIsModalOpen(true);
    };
    
    window.addEventListener('discord-show-modal', handleShowModal);
    return () => window.removeEventListener('discord-show-modal', handleShowModal);
  }, [isConnected, isDismissed]);
  
  // Auto-show modal on page load for logged-in users who haven't connected Discord
  // Uses a ref to track if we've already shown the modal this session
  const hasShownAutoPrompt = useRef(false);
  
  useEffect(() => {
    // Only auto-prompt once per session
    if (hasShownAutoPrompt.current) return;
    
    // Wait for auth state to settle, then check if we should prompt
    const timer = setTimeout(() => {
      if (isAuthenticated && !isConnected && !isDismissed()) {
        hasShownAutoPrompt.current = true;
        setIsModalOpen(true);
      }
    }, 1000); // 1s delay to let page settle
    
    return () => clearTimeout(timer);
  }, [isAuthenticated, isConnected, isDismissed]);
  
  const value = {
    // State
    discordUser,
    isConnected,
    isModalOpen,
    shouldShowPrompt,
    pendingBasketIdent,
    
    // Actions
    promptForConnection,
    connectDiscord,
    disconnectDiscord,
    saveDiscordUser,
    dismissPrompt,
    openModal,
    closeModal,
    getPendingCheckout,
    clearPendingCheckout
  };
  
  return (
    <DiscordContext.Provider value={value}>
      {children}
    </DiscordContext.Provider>
  );
};

export default DiscordContext;
